import { useEffect, useState } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { tokens, Loader, Button } from '@hotjar/hotsauce';

import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { page } from '@hotjar/webapp/domains/common/tracking/segment';
import DeniedIllustration from '@hotjar/shared-assets/images/scale/sso/permission-required.svg';
import { useCurrentSite } from '@hotjar/site';
import { subscriptionAPI } from '@hotjar/webapp/billing/subscription';
import { useSettings } from '@hotjar/webapp/hooks/useSettings';
import { useHelpHotjarUrl } from '@hotjar/translations';

interface AccessDeniedProps {
  variant: ContentVariant;
}

interface ContentProps {
  variant: ContentVariant;
  owner: string;
  onLearnMoreClicked: () => void;
  loading: boolean;
}

export enum ContentVariant {
  billing = 'billing',
  sso = 'sso',
  invoices = 'invoices',
  userAttributes = 'user-attributes',
  subscriptions = 'subscriptions',
}

const LEARN_MORE_LINK = '/articles/115011734788-Team-Member-Permissions';

const BoxTitle = styled.div`
  ${tokens.text('header5')};
`;

const AccessDeniedContainer = styled.div`
  display: flex;
  align-items: center;
  flex-direction: column;
  width: 740px;
  margin: 150px auto;
`;

const AccessContent = styled.div`
  font-size: 16px;
  line-height: 150%;
  letter-spacing: 0.1px;
  color: ${tokens.color.text_default};
  margin-top: ${tokens.spacer.x1};
`;

const AccessDeniedImage = styled.div`
  height: 140px;
  width: 204px;
  margin-bottom: ${tokens.spacer.x3};
  background: url(${DeniedIllustration}) no-repeat;
`;

const LearnMore = styled(Button)`
  padding: ${tokens.spacer.x1} ${tokens.spacer.x2};
  margin-top: ${tokens.spacer.x4};
`;

export const AccessDeniedContent = ({
  owner,
  onLearnMoreClicked,
  variant,
  loading,
}: ContentProps): JSX.Element => {
  const { t } = useTranslation();
  const title = t(`common:settings.access.title.${variant}`);

  return (
    <AccessDeniedContainer>
      <AccessDeniedImage />
      <BoxTitle>{t('common:settings.access.denied-title')}</BoxTitle>
      <AccessContent>
        <Loader isLoading={loading}>
          {owner && (
            <Trans i18nKey="common:settings.access.denied-subcontent">
              Only the Account Owner, <strong>{{ name: owner }}</strong>, can view the
              {{ page: title }} settings page.
            </Trans>
          )}
        </Loader>
      </AccessContent>
      <LearnMore kind="primary" icon="open_new_window" size="m" onClick={onLearnMoreClicked}>
        {t('common:settings.access.learn-more')}
      </LearnMore>
    </AccessDeniedContainer>
  );
};

export const AccessDenied = ({ variant }: AccessDeniedProps): JSX.Element => {
  const [ownerInfo, setOwnerInfo] = useState('');
  const [loading, setLoading] = useState(true);
  const { data: settings } = useSettings();
  const helpHotjarUrl = useHelpHotjarUrl();

  const { click } = useTrack();
  const { data: currentSite } = useCurrentSite();

  const trackClick = (elementName: string) => {
    click(elementName, 'settings.billing.no_access', {
      state_event_was_triggered_on: 'settings.billing.no_access',
    });
  };
  const handleLearnMoreClick = (): void => {
    trackClick(`Learn More Permissions - ${variant}`);
    window.open(`${helpHotjarUrl}${LEARN_MORE_LINK}`, 'hotjar_teamMemberPermissions', 'noopener');
  };

  useEffect(() => {
    // Track Page View
    page('settings.billing.no_access');
  }, []);

  useEffect(() => {
    // Fetch account owner details
    async function getOwnerDetails() {
      if (currentSite?.account) {
        try {
          const {
            accountOwner: { name },
          } = await subscriptionAPI.getInfo(currentSite.account.id, settings);
          setOwnerInfo(name);
          setLoading(false);
        } catch (e) {
          setLoading(false);
        }
      }
    }
    getOwnerDetails();
  }, [currentSite?.account, settings]);

  return (
    <AccessDeniedContent
      owner={ownerInfo}
      onLearnMoreClicked={handleLearnMoreClick}
      variant={variant}
      loading={loading}
    />
  );
};
